/**
 * The public, copy-and-paste URLs of a job — what an employer links to from their own careers page.
 *
 *   job listing   /jobs/:jobId          readable signed out (src/pages/public/PublicJobPage.tsx)
 *   apply         /jobs/:jobId/apply    the application form for a signed-in candidate. A signed-out
 *                                       visitor is NOT bounced to the marketing home page: the same
 *                                       public posting renders here with a "create your account to
 *                                       apply" card, and this exact URL is remembered so signing up
 *                                       returns them to the application (PublicJobGate).
 *   job board     /jobs/<slug>          a company's own careers page once it has a page name
 *                 /careers/:orgId       every open job of one employer, signed out (redirects to
 *                                       /jobs/<slug> once the company chose a page name)
 *
 * One URL per job on purpose: a signed-in candidate opening the same listing link gets the in-app
 * page (match score, their application status); everyone else gets the public page.
 */
import { getPublicBaseUrl } from '../publicBaseUrl';

const PRODUCTION_ORIGIN = 'https://arevina.com';

export const jobPublicPath = (jobId: string): string => `/jobs/${jobId}`;
export const jobApplyPath = (jobId: string): string => `/jobs/${jobId}/apply`;
/** The company's job board: its own page (/jobs/<slug>) when it has a page name, else /careers/<orgId>. */
export const orgJobBoardPath = (orgId: string, slug?: string | null): string => (slug ? `/jobs/${slug}` : `/careers/${orgId}`);

/**
 * Is this URL one of the public share pages — a job's listing (/jobs/:jobId), the apply URL that
 * listing's button points at (/jobs/:jobId/apply), or an employer's job board (/careers/:orgId)?
 * The /jobs/companies map is a candidate screen, not a share link. The app-wide route guard
 * (AuthContext) leaves these alone for non-candidate accounts, so an employer can open their own
 * share link — including its Apply button — and see exactly what the public sees.
 */
export function isPublicSharePath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'careers') return parts.length === 2;
  if (parts[0] !== 'jobs' || parts[1] === 'companies' || !parts[1]) return false;
  return parts.length === 2 || (parts.length === 3 && parts[2] === 'apply');
}

/**
 * Absolute URL for a share link. In production this is always the canonical domain, even when the
 * employer happens to be browsing a Hosting alias (readyresponder.web.app) — a link pasted into
 * someone else's website should be the address we advertise.
 */
export function shareUrl(path: string): string {
  const origin = import.meta.env.VITE_FB_PROJECT_ID === 'ready-responder-prod' ? PRODUCTION_ORIGIN : getPublicBaseUrl();
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}
